import {
  Box,
  Card,
  Link,
  Typography,
  Container,
  alpha,
  styled
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import JWTLogin from '../LoginJWT';

import { useTranslation } from 'react-i18next';
import LogoCoinhouse from '../../../../components/Logo/LogoCoinhouse';
import Scrollbar from '../../../../components/Scrollbar';
import { keyframes } from '@emotion/react';
import { brand, gradientTextOnDark } from '../../../../themes/brandTokens';

const coins = [
  { name: 'Bitcoin', src: '/static/images/brand/coin-btc.webp', size: 150, top: 30, left: 108, delay: 0 },
  { name: 'Ethereum', src: '/static/images/brand/coin-eth.webp', size: 96, top: 110, left: 0, delay: 1.2 },
  { name: 'Solana', src: '/static/images/brand/coin-sol.webp', size: 84, top: 140, left: 262, delay: 2.1 },
  { name: 'USDC', src: '/static/images/brand/coin-usdc.webp', size: 60, top: 0, left: 272, delay: 0.6 }
];

const Content = styled(Box)(
  () => `
    display: flex;
    flex: 1;
    width: 100%;
`
);

const MainContent = styled(Box)(
  ({ theme }) => `
  @media (min-width: ${theme.breakpoints.values.md}px) {
    padding: 0 0 0 440px;
  }
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
`
);

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    width: 440px;
    overflow: hidden;
    color: ${theme.colors.alpha.trueWhite[100]};
    background:
      radial-gradient(420px 320px at 85% 58%, ${alpha(brand.purple, 0.45)}, transparent 70%),
      radial-gradient(360px 260px at 10% 95%, ${alpha(brand.blue, 0.4)}, transparent 70%),
      ${brand.ink};
`
);

const SidebarContent = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing(6)};
`
);

const coinFloat = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-10px); }
`;

const Coin = styled('img')({
  position: 'absolute',
  filter: 'drop-shadow(0 18px 30px rgba(0, 0, 0, 0.45))',
  animation: `${coinFloat} 6s ease-in-out infinite alternate`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  }
});

const TypographyH1 = styled(Typography)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(32)};
    line-height: 1.15;
    color: ${theme.colors.alpha.trueWhite[100]};
`
);

const LoginCover = () => {
  const { t }: { t: any } = useTranslation();

  return (
    <>
      <Helmet>
        <title>Coinhouse Solutions - Login</title>
      </Helmet>
      <Content>
        <SidebarWrapper
          sx={{
            display: { xs: 'none', md: 'flex' }
          }}
        >
          <Scrollbar>
            <SidebarContent>
              <LogoCoinhouse fillColor="white" />
              <Box mt={7}>
                <TypographyH1 variant="h1" sx={{ mb: 5 }}>
                  {t('Empower your business with')}{' '}
                  <Box component="span" sx={gradientTextOnDark}>
                    {t('secure, regulated web3 solutions')}
                  </Box>{' '}
                  {t('for transactions and blockchain use.')}
                </TypographyH1>
                <Box
                  aria-hidden
                  sx={{ position: 'relative', width: 332, height: 230, mb: 4 }}
                >
                  {coins.map((coin) => (
                    <Coin
                      key={coin.name}
                      src={coin.src}
                      alt=""
                      width={coin.size}
                      height={coin.size}
                      sx={{
                        top: coin.top,
                        left: coin.left,
                        animationDelay: `${coin.delay}s`
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 3, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}
                >
                  {t(
                    'Coinhouse Solutions is a service provided by Coinhouse, the first web3 player regulated in France by the AMF. '
                  )}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ color: 'common.white' }}
                >
                  {t(' Looking for the API documentation ?')}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}
                >
                  {t(
                    'Please find all information & documentation for our API'
                  )}
                  .{' '}
                  <Link
                    href="https://coinhouse-solutions.readme.io/"
                    underline="always"
                    sx={{ color: 'common.white', textDecorationColor: 'rgba(255,255,255,0.5)' }}
                  >
                    Read docs
                  </Link>
                </Typography>
              </Box>
            </SidebarContent>
          </Scrollbar>
        </SidebarWrapper>
        <MainContent>
          <Container
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column'
            }}
            maxWidth="sm"
          >
            <Card
              sx={{
                p: { xs: 3, sm: 5 },
                my: 4,
                width: '100%',
                maxWidth: 480
              }}
            >
              <Box textAlign="center">
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
                  <LogoCoinhouse fillColor={brand.ink} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    mb: 1
                  }}
                >
                  {t('Sign in')}
                </Typography>
                <Typography
                  variant="h4"
                  color="text.secondary"
                  fontWeight="normal"
                  sx={{
                    mb: 3
                  }}
                >
                  {t('Fill in the fields below to sign into your account.')}
                </Typography>
              </Box>
              <JWTLogin />
            </Card>
          </Container>
        </MainContent>
      </Content>
    </>
  );
}

export default LoginCover;
